import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { Calendar, MousePointer, PenTool, Type, X } from "lucide-react";
import { Document, Page } from "react-pdf";
import { useEffect, useRef, useState } from "react";

import { pdfjs } from "react-pdf";

// Set up PDF.js worker
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
}

type SignatureField = {
  fieldId: string;
  signerType: "company" | "creator";
  fieldType: "signature" | "date" | "text";
  label: string;
  position: {
    x: number; // Percentage (0-1) relative to page width
    y: number; // Percentage (0-1) relative to page height
    pageNumber: number; // Which page this field is on (1-indexed)
  };
  size: {
    width: number; // Percentage (0-1) relative to page width
    height: number; // Percentage (0-1) relative to page height
  };
  required: boolean;
  placeholder?: string;
};

interface DocumentViewerProps {
  content?: string;
  fileUrl?: string;
  signatureFields: SignatureField[];
  onSignatureFieldsChange: (fields: SignatureField[]) => void;
  selectedFieldType: "signature" | "date" | "text";
  selectedSignerType: "company" | "creator";
}

export const DocumentViewer = ({
  content,
  fileUrl,
  signatureFields,
  onSignatureFieldsChange,
  selectedFieldType,
  selectedSignerType,
}: DocumentViewerProps) => {
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const documentRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Update container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (documentRef.current) {
        const rect = documentRef.current.getBoundingClientRect();
        setContainerWidth(Math.min(800, rect.width - 40)); // 40px for padding
      } else {
        setContainerWidth(Math.min(800, window.innerWidth - 120)); // 120px for sidebar + padding
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  const onPageLoadSuccess = (pageNumber: number) => {
    // Update page width based on the first page that loads
    if (pageNumber === 1 && pageRefs.current[1]) {
      const pageElement = pageRefs.current[1].querySelector(
        ".react-pdf__Page__canvas",
      ) as HTMLCanvasElement;
      if (pageElement) {
        // Page width is handled by containerWidth state
      }
    }
  };

  const addSignatureField = (e: React.MouseEvent, pageNumber: number) => {
    e.stopPropagation();

    const pageRef = pageRefs.current[pageNumber];
    if (!pageRef) return;

    const pageRect = pageRef.getBoundingClientRect();
    const clickX = e.clientX - pageRect.left;
    const clickY = e.clientY - pageRect.top;

    // Convert to percentage of page dimensions
    // Allow fields to be placed closer to edges - accounting for field size
    const fieldWidth = 0.2; // 20% of page width
    const fieldHeight = 0.08; // 8% of page height

    const x = Math.max(0, Math.min(1.0 - fieldWidth, clickX / pageRect.width));
    const y = Math.max(
      0,
      Math.min(1.0 - fieldHeight, clickY / pageRect.height),
    );

    const newField: SignatureField = {
      fieldId: `field_${Date.now()}`,
      signerType: selectedSignerType,
      fieldType: selectedFieldType,
      label: `${selectedSignerType} ${selectedFieldType}`,
      position: {
        x,
        y,
        pageNumber,
      },
      size: {
        width: fieldWidth,
        height: fieldHeight,
      },
      required: true,
      placeholder:
        selectedFieldType === "text" ? `Enter ${selectedFieldType}` : undefined,
    };

    onSignatureFieldsChange([...signatureFields, newField]);
  };

  const updateField = (fieldId: string, updates: Partial<SignatureField>) => {
    onSignatureFieldsChange(
      signatureFields.map((field) =>
        field.fieldId === fieldId ? { ...field, ...updates } : field,
      ),
    );
  };

  const deleteField = (fieldId: string) => {
    onSignatureFieldsChange(
      signatureFields.filter((f) => f.fieldId !== fieldId),
    );
  };

  const handleMouseDown = (
    e: React.MouseEvent,
    fieldId: string,
    action: "drag" | "resize",
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedField(fieldId);

    if (action === "drag") {
      setIsDragging(fieldId);
    } else {
      setIsResizing(fieldId);
    }

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging && !isResizing) return;

    const field = signatureFields.find(
      (f) => f.fieldId === (isDragging || isResizing),
    );
    if (!field) return;

    const pageRef = pageRefs.current[field.position.pageNumber];
    if (!pageRef) return;

    const pageRect = pageRef.getBoundingClientRect();
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    if (isDragging) {
      // Convert delta to percentage changes
      const deltaXPercent = deltaX / pageRect.width;
      const deltaYPercent = deltaY / pageRect.height;

      // Calculate max positions based on field size to prevent overflow
      const maxX = 1.0 - field.size.width; // Ensure field doesn't go beyond right edge
      const maxY = 1.0 - field.size.height; // Ensure field doesn't go beyond bottom edge

      const newX = Math.max(
        0,
        Math.min(maxX, field.position.x + deltaXPercent),
      );
      const newY = Math.max(
        0,
        Math.min(maxY, field.position.y + deltaYPercent),
      );

      updateField(isDragging, {
        position: {
          x: newX,
          y: newY,
          pageNumber: field.position.pageNumber,
        },
      });
    } else if (isResizing) {
      // Convert delta to percentage changes for size
      const deltaWidthPercent = deltaX / pageRect.width;
      const deltaHeightPercent = deltaY / pageRect.height;

      const newWidth = Math.max(
        0.05,
        Math.min(0.5, field.size.width + deltaWidthPercent),
      );
      const newHeight = Math.max(
        0.03,
        Math.min(0.2, field.size.height + deltaHeightPercent),
      );

      updateField(isResizing, {
        size: { width: newWidth, height: newHeight },
      });
    }

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(null);
    setIsResizing(null);
  };

  const getFieldIcon = (type: string) => {
    switch (type) {
      case "signature":
        return <PenTool className="h-3 w-3" />;
      case "date":
        return <Calendar className="h-3 w-3" />;
      case "text":
        return <Type className="h-3 w-3" />;
      default:
        return <MousePointer className="h-3 w-3" />;
    }
  };

  const getFieldColor = (signerType: string) => {
    return signerType === "founder"
      ? "bg-blue-100 border-blue-300 dark:bg-blue-900/30 dark:border-blue-600"
      : "bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-600";
  };

  const renderSignatureFieldsForPage = (pageNumber: number) => {
    return signatureFields
      .filter((field) => field.position.pageNumber === pageNumber)
      .map((field) => {
        const pageRef = pageRefs.current[pageNumber];
        if (!pageRef) return null;

        const pageRect = pageRef.getBoundingClientRect();
        const containerRect = documentRef.current?.getBoundingClientRect();
        if (!containerRect) return null;

        // Calculate absolute position within the page
        const left = field.position.x * pageRect.width;
        const top = field.position.y * pageRect.height;
        const width = field.size.width * pageRect.width;
        const height = field.size.height * pageRect.height;

        return (
          <div
            key={field.fieldId}
            className={`absolute border-2 border-dashed cursor-move ${getFieldColor(field.signerType)}
              ${selectedField === field.fieldId ? "ring-2 ring-blue-500" : ""}`}
            style={{
              left: `${left}px`,
              top: `${top}px`,
              width: `${width}px`,
              height: `${height}px`,
              zIndex: 20,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedField(field.fieldId);
            }}
            onMouseDown={(e) => handleMouseDown(e, field.fieldId, "drag")}
          >
            {/* Field Content */}
            <div className="flex items-center justify-between h-full px-2 bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-600 rounded text-xs">
              <div className="flex items-center gap-1">
                {getFieldIcon(field.fieldType)}
                <span className="font-medium truncate text-gray-900 dark:text-gray-100">
                  {field.label.includes("founder")
                    ? field.label.replace("founder", "company")
                    : field.label}
                </span>
              </div>

              {/* Controls */}
              <button
                className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteField(field.fieldId);
                }}
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {/* Resize Handle */}
            <div
              className="absolute bottom-0 right-0 w-3 h-3 bg-blue-500 cursor-se-resize"
              onMouseDown={(e) => handleMouseDown(e, field.fieldId, "resize")}
            />
          </div>
        );
      });
  };

  return (
    <div className="relative">
      {/* Document Content */}
      <div
        ref={documentRef}
        className="relative bg-white dark:bg-gray-900 border rounded-lg p-4 min-h-[600px] overflow-auto"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {content ? (
          <div
            className="relative mx-auto"
            style={{ width: "fit-content", maxWidth: "100%" }}
          >
            <div
              ref={(ref) => (pageRefs.current[1] = ref)}
              className="relative border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900 p-8 min-h-[800px]"
              style={{ width: `${containerWidth}px` }}
            >
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                {content}
              </pre>

              {/* Clickable overlay for field placement */}
              <div
                className="absolute inset-0 bg-blue-50/10 cursor-crosshair z-10"
                onClick={(e) => addSignatureField(e, 1)}
              />

              {/* Signature Fields for the text content */}
              {renderSignatureFieldsForPage(1)}

              {/* Page indicator */}
              <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                Text Document
              </div>
            </div>
          </div>
        ) : fileUrl ? (
          <div className="space-y-4 max-w-4xl mx-auto">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground">Loading PDF...</p>
                </div>
              </div>
            )}

            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error("Error loading PDF:", error);
                setIsLoading(false);
              }}
              className="w-full"
            >
              {Array.from(new Array(numPages), (_, index) => {
                const pageNumber = index + 1;
                return (
                  <div
                    key={`page_${pageNumber}`}
                    className="relative mb-4 border border-gray-200 dark:border-gray-700 rounded bg-white mx-auto"
                    ref={(ref) => (pageRefs.current[pageNumber] = ref)}
                    style={{ width: "fit-content", maxWidth: "100%" }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      onLoadSuccess={() => onPageLoadSuccess(pageNumber)}
                      width={containerWidth}
                      className="w-full"
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />

                    {/* Clickable overlay for field placement */}
                    <div
                      className="absolute inset-0 bg-blue-50/10 cursor-crosshair z-10"
                      onClick={(e) => addSignatureField(e, pageNumber)}
                    />

                    {/* Signature Fields for this page */}
                    {renderSignatureFieldsForPage(pageNumber)}

                    {/* Page number indicator */}
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Page {pageNumber}
                    </div>
                  </div>
                );
              })}
            </Document>
          </div>
        ) : null}
      </div>

      {/* Instructions */}
      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
          How to use:
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <li>• Click anywhere on a page to add a signature field</li>
          <li>• Select field type and signer type above before clicking</li>
          <li>• Drag existing fields to reposition them</li>
          <li>• Use the bottom-right corner to resize fields</li>
          <li>• Click the X to delete a field</li>
          <li>• At least 1 signature field is required</li>
        </ul>
      </div>

      {/* Status Indicator */}
      <div className="fixed top-20 right-4 z-50 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-2 shadow-lg">
        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
          Click anywhere on a page to add {selectedFieldType} field for{" "}
          {selectedSignerType}
        </p>
        <p className="text-xs text-muted-foreground">
          {signatureFields.length} field
          {signatureFields.length !== 1 ? "s" : ""} added
        </p>
        {numPages > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            PDF: {numPages} page{numPages !== 1 ? "s" : ""} loaded
          </p>
        )}
      </div>
    </div>
  );
};
